import { IVA_SUSCRIPCION } from "./pagos-tarjeta.ts";

/**
 * Renovación automática de la suscripción con la tarjeta guardada (Payphone). Aquí está lo que no depende de la base de datos ni de Payphone:
 * cuándo toca cobrar, cuántas veces se reintenta, qué se le dice a la clínica y qué acepta al activarla.
 * Los textos legales los debe revisar un abogado antes de ofrecer la renovación automática a clientes reales.
 */

/** Cuántos días antes del cobro se le avisa a la clínica por correo. */
export const DIAS_AVISO_COBRO = 5;
/** El día del vencimiento y los días siguientes en que se reintenta si el banco no aprueba (0 = el mismo día del vencimiento). Todos caen dentro de los 7 días de gracia. */
export const DIAS_DE_INTENTO = [0, 1, 3, 5] as const;
export const MAX_INTENTOS_COBRO = DIAS_DE_INTENTO.length;
/** Versión del texto de consentimiento: se guarda con cada aceptación. Si el texto cambia, sube la versión. */
export const VERSION_CONSENTIMIENTO_TARJETA = "2026.1";

const dia = (iso: string) => Date.parse(iso.slice(0, 10) + "T12:00:00Z") / 86400000;

/**
 * ¿Toca cobrar hoy? `intentos` = cuántos cobros automáticos ya se intentaron para ese vencimiento.
 * Se cobra desde el día del vencimiento y, si el banco no aprueba, se reintenta los días 1, 3 y 5 después. Tras el cuarto intento se deja de intentar.
 */
export function tocaCobrar(vence: string, hoy: string, intentos: number): { toca: boolean; intento: number } {
  if (!Number.isInteger(intentos) || intentos < 0 || intentos >= MAX_INTENTOS_COBRO) return { toca: false, intento: intentos + 1 };
  return { toca: dia(hoy) - dia(vence) >= DIAS_DE_INTENTO[intentos], intento: intentos + 1 };
}

/** ¿Es el último intento? Después de este no se vuelve a cobrar solo. */
export const esUltimoIntento = (intento: number) => intento >= MAX_INTENTOS_COBRO;

/** Fecha del siguiente reintento (aaaa-mm-dd) tras fallar el intento número `intento` (1 a 3) de un vencimiento. */
export function fechaDeReintento(vence: string, intento: number): string | null {
  if (intento < 1 || intento >= MAX_INTENTOS_COBRO) return null;
  return new Date((dia(vence) + DIAS_DE_INTENTO[intento]) * 86400000).toISOString().slice(0, 10);
}

const dinero = (centavos: number) => `$ ${(centavos / 100).toFixed(2).replace(".", ",")}`;
export const textoPeriodoCobro = (periodo: "mensual" | "anual") => (periodo === "anual" ? "cada año" : "cada mes");

/** Lo que acepta la clínica al marcar «Renovar automáticamente». Se guarda con la versión, la hora, la persona y el equipo. */
export function textoConsentimientoTarjeta(x: { periodo: "mensual" | "anual"; plan: string; total: number }): string {
  return `Autorizo a Donna a cobrar a mi tarjeta, ${textoPeriodoCobro(x.periodo)}, el valor de mi plan ${x.plan} (${dinero(x.total)} con IVA ${IVA_SUSCRIPCION} %, o el valor vigente de mi plan), `
    + `hasta que yo cancele. Donna guarda una ficha de mi tarjeta que entrega Payphone (nunca el número ni el código de seguridad). `
    + `Me avisarán por correo ${DIAS_AVISO_COBRO} días antes de cada cobro y puedo cancelar la renovación automática cuando quiera en Configuración → Plan y suscripción. `
    + `Solo sirve con tarjetas Visa y Mastercard.`;
}

export interface DatosCorreoCobro { clinica: string; plan: string; total: number; tarjeta: string; vence?: string; hasta?: string; fechaReintento?: string | null; motivo?: string }
const fechaLarga = (iso: string) => {
  const [a, m, d] = iso.slice(0, 10).split("-").map(Number);
  return `${d} de ${["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"][m - 1]} de ${a}`;
};
const PIE = "\n\nPuedes cancelar la renovación automática en Donna: Configuración → Plan y suscripción → «Dejar de renovar automáticamente».";

/** Aviso previo: el cobro se hará en N días. */
export function correoAvisoCobro(x: DatosCorreoCobro): { asunto: string; texto: string } {
  return {
    asunto: `Donna cobrará la renovación de ${x.clinica} el ${fechaLarga(x.vence!)}`,
    texto: `Hola,\n\nEl ${fechaLarga(x.vence!)} Donna cobrará la renovación del plan ${x.plan} de ${x.clinica}: ${dinero(x.total)} (IVA incluido) a tu tarjeta ${x.tarjeta}.${PIE}`,
  };
}
/** Cobro aprobado. */
export function correoCobroAprobado(x: DatosCorreoCobro): { asunto: string; texto: string } {
  return {
    asunto: `Renovamos tu plan de Donna (${dinero(x.total)})`,
    texto: `Hola,\n\nCobramos ${dinero(x.total)} (IVA incluido) a tu tarjeta ${x.tarjeta} por la renovación del plan ${x.plan} de ${x.clinica}. Tu plan está activo hasta el ${fechaLarga(x.hasta!)}.\n\nGracias por seguir con Donna.${PIE}`,
  };
}
/** El banco no aprobó. Si hay fecha de reintento se dice cuándo; si no, se pide pagar a mano. */
export function correoCobroFallido(x: DatosCorreoCobro): { asunto: string; texto: string } {
  const cuando = x.fechaReintento
    ? `Lo volveremos a intentar el ${fechaLarga(x.fechaReintento)}. Mientras tanto puedes pagar tú mismo en Donna: Configuración → Plan y suscripción → «Pagar con tarjeta».`
    : `Ya no lo volveremos a intentar solos. Para no perder el acceso, paga tu plan en Donna: Configuración → Plan y suscripción → «Pagar con tarjeta» (o con otra tarjeta).`;
  return {
    asunto: `No pudimos cobrar la renovación de tu plan de Donna`,
    texto: `Hola,\n\nIntentamos cobrar ${dinero(x.total)} a tu tarjeta ${x.tarjeta} por la renovación del plan ${x.plan} de ${x.clinica}, pero el banco no lo aprobó${x.motivo ? ` (${x.motivo})` : ""}. ${cuando}${PIE}`,
  };
}
